import type { ApiKeyRole } from "@amtana/apikeys-contract";
import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";

// Pure copy for the API keys screen (S4.09): no React, no I/O. The three roles are restated
// here the way `organization-contract` restates them too: a browser package may not import
// another module's browser package, only its contract and client.

export type ApiKeyCopyKey = keyof typeof EN;
export type ApiKeyCatalog = typeof EN;

const EN = {
  roleAdmin: "Admin",
  roleMember: "Member",
  roleViewer: "Viewer",
  screenTitle: "API keys · {product}",
  navApiKeys: "API keys",
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  apiKeysTitle: "API keys",
  apiKeysDescription: "Let a script or an integration call the api as this organization.",
  createKey: "Create key",
  createTitle: "Create an API key",
  createDescription: "Cut to one role, the same three everywhere else: admin, member or viewer.",
  create: "Create",
  name: "Name",
  namePlaceholder: "CI pipeline",
  role: "Role",
  keyCreatedTitle: "Key created",
  keyCreatedDescription: "Copy it now. It will not be shown again; only its prefix stays visible after this.",
  done: "Done",
  apiKeyLabel: "API key",
  revokeTitle: 'Revoke "{name}"?',
  revokeFallbackTitle: "Revoke this key?",
  revokeDescription: "Every request it makes stops working right away.",
  revoke: "Revoke",
  revokeKeyLabel: "Revoke {name}",
  keyRevoked: "Key revoked",
  couldNotCreate: "Could not create that key",
  couldNotRevoke: "Could not revoke that key",
  tableLabel: "API keys",
  noKeysTitle: "No API keys yet",
  noKeysDescription: "Create one to call the api as this organization.",
  columnName: "Name",
  columnRole: "Role",
  columnCreated: "Created",
  columnLastUsed: "Last used",
  columnStatus: "Status",
  columnActions: "Actions",
  never: "Never",
  statusRevoked: "Revoked",
  statusActive: "Active",
} as const;

const ES: Translation<ApiKeyCatalog> = {
  roleAdmin: "Admin",
  roleMember: "Miembro",
  roleViewer: "Lector",
  screenTitle: "Claves de API · {product}",
  navApiKeys: "Claves de API",
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Ajustes",
  apiKeysTitle: "Claves de API",
  apiKeysDescription: "Deja que un script o una integración llame a la API como esta organización.",
  createKey: "Crear clave",
  createTitle: "Crear una clave de API",
  createDescription: "Limitada a un rol, los mismos tres de siempre: admin, miembro o lector.",
  create: "Crear",
  name: "Nombre",
  namePlaceholder: "Pipeline de CI",
  role: "Rol",
  keyCreatedTitle: "Clave creada",
  keyCreatedDescription: "Cópiala ahora. No se mostrará de nuevo; después solo quedará visible su prefijo.",
  done: "Listo",
  apiKeyLabel: "Clave de API",
  revokeTitle: '¿Revocar "{name}"?',
  revokeFallbackTitle: "¿Revocar esta clave?",
  revokeDescription: "Todas las solicitudes que haga dejarán de funcionar de inmediato.",
  revoke: "Revocar",
  revokeKeyLabel: "Revocar {name}",
  keyRevoked: "Clave revocada",
  couldNotCreate: "No pudimos crear esa clave",
  couldNotRevoke: "No pudimos revocar esa clave",
  tableLabel: "Claves de API",
  noKeysTitle: "Aún no hay claves de API",
  noKeysDescription: "Crea una para llamar a la API como esta organización.",
  columnName: "Nombre",
  columnRole: "Rol",
  columnCreated: "Creada",
  columnLastUsed: "Último uso",
  columnStatus: "Estado",
  columnActions: "Acciones",
  never: "Nunca",
  statusRevoked: "Revocada",
  statusActive: "Activa",
};

export const API_KEY_CATALOGS: Catalogs<ApiKeyCatalog> = { en: EN, es: ES };

const ROLE_KEY: Readonly<Record<ApiKeyRole, PlainKey<ApiKeyCatalog>>> = {
  admin: "roleAdmin",
  member: "roleMember",
  viewer: "roleViewer",
};

/** The role's name in the active language. */
export function apiKeyRoleLabel(role: ApiKeyRole, t: Translate<ApiKeyCatalog>): string {
  return t(ROLE_KEY[role]);
}

/** The three roles, admin first, as picker options. */
export function apiKeyRoleOptions(
  t: Translate<ApiKeyCatalog>,
): ReadonlyArray<{ readonly value: ApiKeyRole; readonly label: string }> {
  return [
    { value: "admin", label: apiKeyRoleLabel("admin", t) },
    { value: "member", label: apiKeyRoleLabel("member", t) },
    { value: "viewer", label: apiKeyRoleLabel("viewer", t) },
  ];
}
